import { isVersionGreaterOrEqualThan } from '@djs/version';
import { canUseDOM } from 'node_modules/@vkontakte/vkjs/lib/other';

/**
 * В Safari на iPadOS поле User Agent содержит почти такую же информацию, что и в Safari на MacOS.
 * Из-за чего мы не можем ориентироваться на User Agent.
 *
 * Вместо этого мы пробуем определять есть ли событие 'ontouchend' в объекте document.
 *
 * см. https://developer.apple.com/forums/thread/119186?answerId=705140022#705140022
 */
export function checkIPadOS(ua: string): boolean {
  if (!canUseDOM) {
    return false;
  }

  const isNotIOS = !/iPad|iPhone|iPod/i.test(ua);
  const isMacOS = /Mac OS/i.test(ua);

  return isNotIOS && isMacOS && 'ontouchend' in document;
}

/**
 * Получает версию iOS по User Agent.
 * iPadOS тоже определяет с помощью этой функции, приравниваем его к iOS,
 * поскольку они идут вровень и их отличие не предоставляет для нас интерес
 */
export function getIOSVersion(ua: string): string | null {
  const iOSDashVersion = ua.match(/(?:iPad|iPhone|iPod)? OS (\d+(?:_\d+)*)/)?.[1].replaceAll('_', '.');

  if (iOSDashVersion) {
    const iosAfterSlashVersion = ua.match(/Version\/(\d+(?:\.\d+)*)/)?.[1];

    // Обход залипания версии iOS 18.6 или 18.7, начиная с iOS 26. Подробнее:
    // - https://www.appsflyer.com/blog/measurement-analytics/safari-ios26-user-agent/
    // - https://docs.uaparser.dev/guides/how-to-detect-ios-26-using-javascript.html
    return iosAfterSlashVersion &&
      isVersionGreaterOrEqualThan(iOSDashVersion, '18.6') &&
      !isVersionGreaterOrEqualThan(iOSDashVersion, '26.0')
      ? iosAfterSlashVersion
      : iOSDashVersion;
  }

  const ipadAfterSlashVersion = checkIPadOS(ua) ? ua.match(/Version\/(\d+(?:\.\d+)*)/)?.[1] : void 0;

  if (ipadAfterSlashVersion) {
    return ipadAfterSlashVersion;
  }

  // Случай, когда userAgent имеет совсем другую семантическую структуру в клиентах вк (app, vkme, dating)
  // см тесткейсы
  const dotVersion = ua.match(/(?:iOS|iPadOS) (\d+(?:\.\d+)*)/)?.[1];

  if (dotVersion) {
    return dotVersion;
  }

  return null;
}

const isTelegram = (): boolean =>
  typeof (window as Window & { TelegramWebviewProxy?: unknown }).TelegramWebviewProxy !== 'undefined';

// UA-маркеры in-app браузеров соцсетей и мессенджеров
const SOCIAL_IN_APP_MARKERS = [
  'FBAN',
  'FBAV',
  'Instagram',
  'VKWebView',
  'VKAndroid',
  'OKApp',
  'WhatsApp',
  'TwitteriPhone',
  'TwitterAndroid',
  'musical_ly',
  'BytedanceWebview',
  'Snapchat',
  'MicroMessenger',
  'Line/',
  'com\\.vk\\.vkclient',
  'com\\.vk\\.vkme',
  'ru\\.odnoklassniki\\.iphone',
  'com\\.vk\\.dating',
  'ru\\.ok\\.dating',
];

const IN_APP_UA_REGEXP = new RegExp(SOCIAL_IN_APP_MARKERS.join('|'), 'i');

const checkIsIOSInApp = (ua: string) => isTelegram() || IN_APP_UA_REGEXP.test(ua);

/**
 * Список проверок для определения названия браузера и его версии.
 * Порядок имеет значение (проверки останавливливаются на первой найденной регулярке)
 */
const IOS_BROWSER_VERSION_DETECTION_ORDER: Array<[IOSBrowserName, (ua: string) => string | void]> = [
  // Браузер внутри приложения Яндекс (Яндекс с Алисой и т.п.) тоже содержит YaBrowser/,
  // поэтому его исключаем из определения отдельного Яндекс.Браузера
  ['yandex', (ua) => (/YaApp_iOS\//.test(ua) ? void 0 : ua.match(/YaBrowser\/(\d+(?:\.\d+)*)/)?.[1])],
  ['chrome', (ua) => ua.match(/CriOS\/(\d+(?:\.\d+)*)/)?.[1]],
  // Эта проверка должна всегда быть в конце списка, потому что у других браузеров
  // в составе UserAgent может мелькать упоминание Safari (см тест кейсы)
  ['safari', (ua) => (checkIsIOSInApp(ua) ? void 0 : ua.match(/Version\/(\d+(?:\.\d+)*).*Safari\//)?.[1])],
];

type IOSBrowserName = 'safari' | 'chrome' | 'yandex' | 'other';

export interface IOSBrowser {
  name: IOSBrowserName;
  version: string;
}

export function getIOSBrowser(ua: string): IOSBrowser | null {
  if (!getIOSVersion(ua)) {
    return null;
  }

  for (const [name, versionGetter] of IOS_BROWSER_VERSION_DETECTION_ORDER) {
    const version = versionGetter(ua);

    if (version) {
      return { name, version };
    }
  }

  return { name: 'other', version: '0.0' };
}
